import { Link, NavLink, useLocation } from 'react-router-dom';
import { IoMdMenu, IoMdClose } from 'react-icons/io';
import Logo from '@/assets/brand/trhine-logo.png';
import ServicesDropdown from './ServicesDropdown';
import { useNavbar } from '@/components/NavbarContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const { t } = useTranslation();
  const { isMenuOpen, setIsMenuOpen } = useNavbar();
  const location = useLocation();

  // Check if we're on the contact section of the home page
  const isContactSection = location.pathname === '/' && location.hash === '#contact';

  // Check if we're on the home page but not on the contact section
  const isHomeActive = location.pathname === '/' && !isContactSection;

  // Add this to prevent clicks on the navbar from closing it
  const handleNavbarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-300/200 shadow-sm" onClick={handleNavbarClick}>
      <div className="flex items-center justify-between py-2 px-8">
        {/* Logo */}
        <div className="flex items-center">
          <Link 
            to="/" 
            className="flex items-center"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault(); // Prevent default navigation if already on home page
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img src={Logo} alt="TRhine Logo" className="h-12 w-12 md:h-20 md:w-20" />
            <p className="hidden md:block text-xl md:text-2xl font-bold p-2">
              <span className="text-red-600">TR</span>
              <span className="text-black">hine Services</span>
            </p>
          </Link>
        </div>

        {/* Mobile Menu Button and Language Switcher */}
        <div className="md:hidden flex items-center gap-3">
          <LanguageSwitcher variant="mobile" />
          <button className="text-2xl" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <IoMdClose /> : <IoMdMenu />}
          </button>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-8 items-center">
          <NavLink
            to="/"
            className={({ isActive }) =>
              (isActive && isHomeActive) ? "text-primary-blue" : "text-black hover:text-gray-400 transition-colors"
            }
            end // Only match exactly '/' without hash
            onClick={() => {
              if (window.location.pathname === '/') {
                // If already on home page, just scroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
              // If on another page, the NavLink will handle navigation to home
            }}
          >
            {t('navbar.mainMenu')}
          </NavLink>
          <ServicesDropdown />
          <NavLink
            to="/#contact"
            className={isContactSection ? "text-primary-blue" : "text-black hover:text-gray-400 transition-colors"}
          >
            {t('navbar.contactUs')}
          </NavLink>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden gap-4 fixed top-0 right-0 h-full w-64 bg-white/98 backdrop-blur-lg border-l border-gray-200/50 shadow-2xl transform ${isMenuOpen ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 p-5 flex flex-col`}>
        {/* Close Button */}
        <button className="self-end text-2xl mb-4" onClick={() => setIsMenuOpen(false)}>
          <IoMdClose />
        </button>
        <NavLink
          to="/"
          className={({isActive}) => [
            isActive && isHomeActive ? "text-primary-blue" : "text-black",
            "block"
          ].join(" ")}
          onClick={() => {
            setIsMenuOpen(false);
            if (window.location.pathname === '/') {
              // If already on home page, scroll to top
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          end
        >
          {t('navbar.mainMenu')}
        </NavLink>
        <ServicesDropdown />
        <NavLink
          to="/#contact"
          className={isContactSection ? "text-primary-blue block" : "text-black block"}
          onClick={() => setIsMenuOpen(false)}
        >
          {t('navbar.contactUs')}
        </NavLink>
      </div>
    </nav>
  );
};

export default Navbar;
